import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const EVENT_TYPES = [
  "login", "call_ringing", "call_accepted", "call_rejected", "call_ended",
  "call_no_answer", "door_unlock", "qr_entry", "pass_revoked", "force_logout",
];

const PAGE_SIZE = 50;

export function AdminActivityTab({ buildings = [] }) {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [buildingId, setBuildingId] = useState("");
  const [userId, setUserId] = useState("");
  const [eventType, setEventType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (buildingId) params.building_id = buildingId;
      if (userId) params.user_id = userId;
      if (eventType) params.event_type = eventType;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(`${dateTo}T23:59:59`).toISOString();
      const { data } = await api.get("/platform/activity", { params });
      setItems(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load activity");
    } finally { setLoading(false); }
  }, [buildingId, userId, eventType, dateFrom, dateTo, page]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/platform/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  function resetFilters() {
    setBuildingId(""); setUserId(""); setEventType("");
    setDateFrom(""); setDateTo(""); setPage(0);
  }

  const buildingCodeById = (id) => buildings.find((b) => b.id === id)?.code || "—";
  const userById = (id) => users.find((u) => u.id === id);

  return (
    <div className="space-y-4" data-testid="admin-activity-tab">
      <div className="card-stroke p-4 grid grid-cols-2 md:grid-cols-6 gap-3">
        <div>
          <Label className="label-tiny">Building</Label>
          <Select value={buildingId || "all"} onValueChange={(v)=>{ setBuildingId(v==="all"?"":v); setPage(0); }}>
            <SelectTrigger className="mt-2" data-testid="activity-filter-building"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {buildings.map((b)=>(<SelectItem key={b.id} value={b.id}>{b.code}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-tiny">User</Label>
          <Select value={userId || "all"} onValueChange={(v)=>{ setUserId(v==="all"?"":v); setPage(0); }}>
            <SelectTrigger className="mt-2" data-testid="activity-filter-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {users.slice(0, 200).map((u)=>(<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-tiny">Event</Label>
          <Select value={eventType || "all"} onValueChange={(v)=>{ setEventType(v==="all"?"":v); setPage(0); }}>
            <SelectTrigger className="mt-2" data-testid="activity-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {EVENT_TYPES.map((t)=>(<SelectItem key={t} value={t}>{t}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-tiny">From</Label>
          <Input type="date" value={dateFrom} onChange={(e)=>{ setDateFrom(e.target.value); setPage(0); }}
                 className="mt-2 font-mono" data-testid="activity-filter-from"/>
        </div>
        <div>
          <Label className="label-tiny">To</Label>
          <Input type="date" value={dateTo} onChange={(e)=>{ setDateTo(e.target.value); setPage(0); }}
                 className="mt-2 font-mono" data-testid="activity-filter-to"/>
        </div>
        <div className="flex items-end gap-2">
          <Button variant="outline" onClick={resetFilters} data-testid="activity-reset">Reset</Button>
          <Button variant="outline" onClick={load} data-testid="activity-refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""}/>
          </Button>
        </div>
      </div>

      <div className="card-stroke p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="activity-table">
          <thead>
            <tr className="label-tiny text-left border-b border-border">
              <th className="py-3 px-4">Time</th><th>Type</th><th>Building</th>
              <th>Visitor / subject</th><th>Actor</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="py-8 text-center text-muted-foreground" data-testid="activity-empty">
                {loading ? "Loading…" : "No matching activity."}
              </td></tr>
            ) : items.map((a) => {
              const actorId = a.actor_id || a.resident_id || a.guard_id || a.user_id;
              const actor = actorId ? userById(actorId) : null;
              return (
                <tr key={a.id} className="border-b border-border last:border-0" data-testid={`activity-row-${a.id}`}>
                  <td className="py-3 px-4 font-mono text-xs text-muted-foreground">
                    {a.created_at ? new Date(a.created_at).toLocaleString() : "—"}
                  </td>
                  <td>
                    <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                      {a.type}
                    </span>
                  </td>
                  <td className="font-mono text-xs">{buildingCodeById(a.building_id)}</td>
                  <td className="text-sm">{a.visitor_name || a.user_name || a.pass_code || "—"}</td>
                  <td className="text-xs">{actor?.name || a.actor_name || a.resident_name || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between" data-testid="activity-pagination">
        <div className="text-xs font-mono text-muted-foreground">
          Page {page + 1} · showing {items.length} rows
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page === 0 || loading}
                  onClick={()=>setPage((p)=>Math.max(0, p-1))} data-testid="activity-prev">
            <ChevronLeft size={14} className="mr-1"/> Prev
          </Button>
          <Button size="sm" variant="outline" disabled={!hasMore || loading}
                  onClick={()=>setPage((p)=>p+1)} data-testid="activity-next">
            Next <ChevronRight size={14} className="ml-1"/>
          </Button>
        </div>
      </div>
    </div>
  );
}
